import { Head, Link, router, usePage } from '@inertiajs/react'
import { useState } from 'react'
import Layout from '../../components/Layout'
import MemberPicker from '../../components/teams/MemberPicker'
import { sortPeople } from '../../utils/peopleSort'

interface OrgUser {
  id: number
  full_name: string
  email: string
  initials: string
}

interface MembershipRow {
  user_id: number
  role: string
  name: string
  email: string
  initials: string
  avatar_initials: string
}

interface TeamData {
  id: number
  name: string
  description: string | null
  is_private: boolean
  member_count: number
}

interface Props {
  team: TeamData
  org_users: OrgUser[]
  memberships: MembershipRow[]
  can_delete: boolean
}

const ROLE_OPTIONS = [
  { value: 'leader', label: 'Leader' },
  { value: 'facilitator', label: 'Facilitator' },
  { value: 'member', label: 'Member' },
]

export default function TeamsEdit({ team, org_users, memberships: initialMemberships, can_delete }: Props) {
  const { flash } = usePage().props as any
  const [name, setName] = useState(team.name)
  const [description, setDescription] = useState(team.description || '')
  const [isPrivate, setIsPrivate] = useState(team.is_private)
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [memberships, setMemberships] = useState<MembershipRow[]>(initialMemberships)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showAddMembers, setShowAddMembers] = useState(false)
  const [newMemberIds, setNewMemberIds] = useState<number[]>([])

  const existingUserIds = memberships.map(m => m.user_id)
  const availableToAdd = sortPeople(org_users.filter(u => !existingUserIds.includes(u.id)))

  const updateRole = (userId: number, role: string) => {
    setMemberships(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m))
  }

  const removeMember = (userId: number) => {
    const leaders = memberships.filter(m => m.role === 'leader')
    const isLastLeader = leaders.length === 1 && leaders[0].user_id === userId
    if (isLastLeader) {
      alert('Cannot remove the last team leader. Assign another leader first.')
      return
    }
    setMemberships(prev => prev.filter(m => m.user_id !== userId))
  }

  const addSelectedMembers = () => {
    const toAdd = org_users
      .filter(u => newMemberIds.includes(u.id))
      .map(u => ({
        user_id: u.id,
        role: 'member',
        name: u.full_name,
        email: u.email,
        initials: u.initials,
        avatar_initials: u.initials
      }))
    setMemberships(prev => [...prev, ...toAdd])
    setNewMemberIds([])
    setShowAddMembers(false)
  }

  const handleSubmit = () => {
    setProcessing(true)
    router.put(`/teams/${team.id}`, {
      team: { name, description, is_private: isPrivate },
      memberships: memberships.map(m => ({ user_id: m.user_id, role: m.role }))
    }, {
      onError: (errs) => setErrors(errs),
      onFinish: () => setProcessing(false)
    })
  }

  const handleDelete = () => {
    router.delete(`/teams/${team.id}`)
  }

  return (
    <Layout>
      <Head title={`Edit ${team.name} — Pinnacle`} />
      <div className="max-w-2xl mx-auto px-6 py-8">

        <nav className="text-sm mb-6 text-text-muted">
          <Link href="/teams" className="hover:text-text-primary">Teams</Link>
          <span className="mx-2">›</span>
          <Link href={`/teams/${team.id}`} className="hover:text-text-primary">{team.name}</Link>
          <span className="mx-2">›</span>
          <span className="text-text-primary">Edit</span>
        </nav>

        {flash?.notice && (
          <div className="mb-4 px-4 py-3 bg-[rgba(21,83,46,0.15)] border border-[#15532E]/20 rounded text-sm text-[#15532E]">{flash.notice}</div>
        )}

        <h1 className="text-2xl font-semibold text-text-primary font-heading mb-6">Edit Team</h1>

        <div className="bg-white rounded-lg border border-stone shadow-sm p-6 space-y-6 mb-6">

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">
              Team Name <span className="text-pinnacle-red">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none"
            />
            {errors.name && <p className="text-pinnacle-red text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">Visibility</label>
            <div className="flex gap-4">
              {[
                { value: false, label: 'Public', description: 'All organization members can see this team' },
                { value: true, label: 'Private', description: 'Only members can see this team' },
              ].map(option => (
                <label
                  key={String(option.value)}
                  className={[
                    'flex-1 border rounded-lg p-4 cursor-pointer transition-colors',
                    isPrivate === option.value ? 'border-pinnacle-red bg-[rgba(237,28,36,0.15)]' : 'border-stone hover:border-charcoal'
                  ].join(' ')}
                >
                  <input type="radio" className="hidden" checked={isPrivate === option.value} onChange={() => setIsPrivate(option.value)} />
                  <p className="font-semibold text-sm text-text-primary">{option.label}</p>
                  <p className="text-sm text-text-secondary mt-1">{option.description}</p>
                </label>
              ))}
            </div>
          </div>

        </div>

        <div className="bg-white rounded-lg border border-stone shadow-sm overflow-hidden mb-6">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone">
            <span className="text-sm font-bold uppercase tracking-widest text-text-secondary">Members ({memberships.length})</span>
            <button
              onClick={() => setShowAddMembers(!showAddMembers)}
              className="text-sm font-semibold text-text-primary hover:underline"
            >
              + Add Members
            </button>
          </div>

          {showAddMembers && availableToAdd.length > 0 && (
            <div className="px-5 py-4 border-b border-stone bg-charcoal/5">
              <p className="text-sm text-text-secondary mb-3">Select members to add:</p>
              <MemberPicker
                orgUsers={availableToAdd}
                selectedIds={newMemberIds}
                onChange={setNewMemberIds}
              />
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => { setShowAddMembers(false); setNewMemberIds([]) }} className="text-sm text-text-secondary hover:text-text-primary">Cancel</button>
                <button
                  onClick={addSelectedMembers}
                  disabled={newMemberIds.length === 0}
                  className="px-3 py-1.5 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark disabled:opacity-40"
                >
                  Add {newMemberIds.length > 0 ? `${newMemberIds.length} ` : ''}Member{newMemberIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}

          <div className="divide-y divide-stone">
            {sortPeople(memberships).map(m => (
              <div key={m.user_id} className="flex items-center gap-3 px-5 py-3">
                <div className="w-8 h-8 rounded-full bg-charcoal flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                  {m.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{m.name}</p>
                  <p className="text-sm text-text-secondary truncate">{m.email}</p>
                </div>
                <select
                  value={m.role}
                  onChange={e => updateRole(m.user_id, e.target.value)}
                  className="text-sm font-medium px-2 py-1.5 border border-stone rounded bg-white focus:border-charcoal focus:outline-none"
                >
                  {ROLE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeMember(m.user_id)}
                  className="opacity-30 hover:opacity-70 hover:text-pinnacle-red transition-colors flex-shrink-0"
                  aria-label="Remove member"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 mb-8">
          <Link href={`/teams/${team.id}`} className="px-4 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-[#F8F9FA] transition-colors">
            Cancel
          </Link>
          <button
            onClick={handleSubmit}
            disabled={processing || !name.trim()}
            className="px-5 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors disabled:opacity-40"
          >
            {processing ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {can_delete && (
          <div className="border border-[#ED1C24]/20 rounded-lg p-5 bg-[rgba(237,28,36,0.15)]">
            <h3 className="text-sm font-semibold text-pinnacle-red mb-1">Danger Zone</h3>
            <p className="text-sm text-text-secondary mb-4">
              Deleting this team is permanent and cannot be undone. All meetings, topics, rocks, and action items belonging to this team will also be deleted.
            </p>
            {!showConfirmDelete ? (
              <button
                onClick={() => setShowConfirmDelete(true)}
                className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors"
              >
                Delete Team
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-sm text-text-primary font-medium">Are you sure?</span>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors"
                >
                  Yes, Delete Team
                </button>
                <button
                  onClick={() => setShowConfirmDelete(false)}
                  className="px-4 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-[#F8F9FA] transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
